import axios, { AxiosResponse } from 'axios';

const MAIN_HOST = (window as any).env.MAIN_HOST;

export type BulletinType = {
  bid: number;
  date: string;
  title: string;
  tag: string;
  text: string;
};

export type BulletinListType = {
  bid: number;
  date: string;
  title: string;
  tag: string;
  text: string;
}[];

export type DocumentType = {
  did: number;
  date: string;
  title: string;
  link: string;
  file_size?: number | null;
};

export type DocumentFormType = {
  did: number;
  title: string;
  file: File | null;
};

export type IssuerType = {
  id: string;
  name: string;
  acronym: string;
  cert_name: string;
  plan_start_date: string | null;
  issued_count: number;
  verified_count: number;
  plan_id: number;
  plan_code: string;
  plan_name_zh: string;
  annual_limit: number | null;
  ips: string[];
};

export type IssuerListType = IssuerType[];

export type IssuerEditPayload = {
  issuer_id?: string;
  name?: string;
  acronym?: string;
  cert_name?: string;
  plan_start_date?: string;
  ips?: string;
  plan_code?: string;
};

export type PlanType = {
  id: number;
  code: string;
  name_zh: string;
  annual_limit: number | null;
  issuer_count: number;
};

export type PlanListType = PlanType[];

export type PlanEditPayload = {
  plan_id?: number;
  code?: string;
  name_zh?: string;
  annual_limit?: string;
};

export type HomeStatConfigType = {
  cert_issuance_units: number;
  vc_issuance_units: number;
  vc_verification_units: number;
};

interface ApiResponse {
  applied: boolean;
  error_msg: string;
}

interface BulletinResponse extends ApiResponse {
  bid?: number;
  date?: string;
}

interface DocumentResponse extends ApiResponse {
  did?: number;
  date?: string;
  link?: string;
  file_size?: number | null;
}

interface IssuerListResponse extends ApiResponse {
  count: number;
  list: IssuerListType;
}

interface IssuerEditResponse extends ApiResponse {
  id?: string;
  name?: string;
  acronym?: string;
  cert_name?: string;
  plan_start_date?: string | null;
  issued_count?: number;
  verified_count?: number;
  plan_code?: string;
  plan_name_zh?: string;
  annual_limit?: number | null;
}

interface PlanListResponse extends ApiResponse {
  count: number;
  list: PlanListType;
}

interface PlanEditResponse extends ApiResponse {
  id?: number;
  code?: string;
  name_zh?: string;
  annual_limit?: number | null;
}

/**
 * Logout from the backend.
 * This function makes a GET request to the /accounts/logout/ endpoint.
 */
export const logout = async (): Promise<ApiResponse> => {
  const url = `${MAIN_HOST}/accounts/logout/`;
  return await axios.get(url);
};

/**
 * Check the login status.
 * This function makes a GET request to the /backstage/authcheck/ endpoint.
 */
export const authCheck = async (): Promise<ApiResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/authcheck/`;
  try {
    const response: AxiosResponse = await axios.get(url);

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Validate a comma-separated IPv4 string.
 * Returns an error message string if invalid, null if valid.
 */
export const validateIpv4List = (ips: string): string | null => {
  const ipv4Regex =
    /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)$/;
  const list = ips
    .split(',')
    .map((ip) => ip.trim())
    .filter(Boolean);
  if (list.length === 0) return 'IP 位址不得為空';
  for (const ip of list) {
    if (!ipv4Regex.test(ip)) return `IP 格式不正確：${ip}`;
  }
  return null;
};

export const downloadStatistic = async () => {
  const url = `${MAIN_HOST}/backstage/csvstat/`;
  return await axios.get(url);
};

/**
 * Manage the bulletin (create/update).
 * This function makes a POST request to the /backstage/bulletin/ endpoint.
 * @param {string} passwd - user password
 * @param {string} tag - bulletin tag for adding/updating
 * @param {string} title - bulletin title for adding/updating
 * @param {string} text - bulletin text for adding/updating
 * @param {number} [bid] - bulletin id to update/delete (omit this for adding a new bulletin)
 *
 * 1. Create mode: The bid parameter is not passed, and the other three parameters are passed,
 * which are new announcements, and the return value will have the bid and date of the new announcement.
 * 2. Update mode: The bid parameter and the other three parameters are passed.
 * In order to update the announcement, the returned value will have an updated date
 */
export const editBulletin = async (
  passwd: string,
  tag: string,
  title: string,
  text: string,
  bid?: number,
): Promise<BulletinResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/bulletin/`;
  const formData = new FormData();

  formData.append('passwd', passwd);
  formData.append('tag', tag);
  formData.append('title', title);
  formData.append('text', text);
  // Append 'bid' only if it is provided
  if (bid) {
    formData.append('bid', bid.toString());
  }

  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Delete the bulletin.
 * This function makes a POST request to the /backstage/bulletin/ endpoint.
 * @param {string} passwd - user password
 * @param {number} bid - bulletin id to update/delete (omit this for adding a new bulletin)
 *
 * Only the bid parameter is passed to delete the announcement.
 */
export const deleteBulletin = async (passwd: string, bid: number): Promise<BulletinResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/bulletin/`;
  const formData = new FormData();

  formData.append('passwd', passwd);
  formData.append('bid', bid.toString());

  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Manage the documents (create/update).
 * This function makes a POST request to the /backstage/document/ endpoint.
 * @param {string} passwd - user password
 * @param {string} title - document title for adding/updating
 * @param {File} file - document file for adding/updating
 * @param {number} [did] - document id to update (omit this for adding a new document)
 *
 * 1. Create mode: The did parameter is not passed, but the other two parameters are passed,
 * which are added files, and the returned value will have the did, date and link of the newly added file.
 * 2. Update mode: There is the did parameter and the other two parameters.
 * In order to update the file, the returned value will have the updated date and link
 */
export const editDocument = async (
  passwd: string,
  title: string,
  file: File | null,
  did?: number,
): Promise<DocumentResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/document/`;
  const formData = new FormData();
  formData.append('passwd', passwd);
  formData.append('title', title);
  if (file) formData.append('file', file);
  if (did) formData.append('did', did.toString());

  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Delete the documents.
 * This function makes a POST request to the /backstage/document/ endpoint.
 * @param {string} passwd - user password
 * @param {number} did - document id
 *
 * Only the did parameter is passed to delete the file
 */
export const deleteDocument = async (passwd: string, did: number): Promise<DocumentResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/document/`;
  const formData = new FormData();
  formData.append('passwd', passwd);
  if (did) {
    formData.append('did', did.toString());
  }

  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getIssuers = async (): Promise<IssuerListResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/issuer-list/`;
  try {
    const response: AxiosResponse = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Create / Update an issuer via a single endpoint.
 *
 * Create:
 *   issuer_id omitted
 *   name + acronym + ips required
 *
 * Update:
 *   issuer_id provided
 *   pass only fields to change
 */
export const editIssuer = async (
  passwd: string,
  payload: IssuerEditPayload,
): Promise<IssuerEditResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/issuer/`;
  const formData = new FormData();
  formData.append('passwd', passwd);
  if (payload.issuer_id !== undefined) formData.append('issuer_id', payload.issuer_id);
  if (payload.name !== undefined) formData.append('name', payload.name);
  if (payload.acronym !== undefined) formData.append('acronym', payload.acronym);
  if (payload.cert_name !== undefined) formData.append('cert_name', payload.cert_name);
  if (payload.plan_start_date !== undefined) formData.append('plan_start_date', payload.plan_start_date);
  if (payload.ips !== undefined) formData.append('ips', payload.ips);
  if (payload.plan_code !== undefined) formData.append('plan_code', payload.plan_code);
  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Fetch the list of plans.
 * This function makes a GET request to the /backstage/plan-list/ endpoint.
 */
export const getPlans = async (): Promise<PlanListResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/plan-list/`;
  try {
    const response: AxiosResponse = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Create / Update / Delete a plan via a single endpoint.
 *
 * Create:
 *   plan_id omitted
 *   code + name_zh required
 *
 * Update:
 *   plan_id provided + name_zh and/or annual_limit
 *
 * Delete:
 *   plan_id provided, no other fields
 */
export const editPlan = async (passwd: string, payload: PlanEditPayload): Promise<PlanEditResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/plan/`;
  const formData = new FormData();
  formData.append('passwd', passwd);
  if (payload.plan_id !== undefined) formData.append('plan_id', String(payload.plan_id));
  if (payload.code !== undefined) formData.append('code', payload.code);
  if (payload.name_zh !== undefined) formData.append('name_zh', payload.name_zh);
  if (payload.annual_limit !== undefined) formData.append('annual_limit', payload.annual_limit);
  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Update homepage statistics display config.
 * This function makes a POST request to the /backstage/home-stat-config/ endpoint.
 * @param {HomeStatConfigType} payload - the three unit counts to update
 * @param {string} passwd - admin password
 */
export const updateHomeStatConfig = async (
  payload: HomeStatConfigType,
  passwd: string,
): Promise<ApiResponse | undefined> => {
  const url = `${MAIN_HOST}/backstage/home-stat-config/`;
  const formData = new FormData();
  formData.append('cert_issuance_units', String(payload.cert_issuance_units));
  formData.append('vc_issuance_units', String(payload.vc_issuance_units));
  formData.append('vc_verification_units', String(payload.vc_verification_units));
  formData.append('passwd', passwd);

  try {
    const response: AxiosResponse = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
